<script>
import { GlButton, GlCollapsibleListbox, GlFormInput, GlFormGroup, GlIcon } from '@gitlab/ui';
import { sprintf, __, n__, s__ } from '~/locale';
import { SEVERITY_CLASS_NAME_MAP } from 'ee/vue_shared/security_reports/components/constants';
import { SEVERITY_LEVELS } from 'ee/security_dashboard/constants';
import vulnerabilitiesSeverityOverride from 'ee/security_dashboard/graphql/mutations/vulnerabilities_severity_override.mutation.graphql';
import toast from '~/vue_shared/plugins/global_toast';
import * as Sentry from '~/sentry/sentry_browser_wrapper';

export default {
  components: {
    GlButton,
    GlCollapsibleListbox,
    GlFormInput,
    GlFormGroup,
    GlIcon,
  },
  inject: {
    vulnerabilitiesQuery: {
      required: true,
    },
    vulnerabilitiesCountsQuery: {
      required: false,
      default: null,
    },
  },
  props: {
    selectedVulnerabilities: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      isSubmitting: false,
      selectedSeverity: null,
      comment: null,
      isValidSeverity: true,
      isValidComment: true,
    };
  },
  computed: {
    severityToggleText() {
      return SEVERITY_LEVELS[this.selectedSeverity] || this.$options.i18n.severityTogglePlaceholder;
    },
    severityItems() {
      return Object.entries(SEVERITY_LEVELS).map(([value, text]) => ({
        value,
        text,
        icon: `severity-${value}`,
        iconColor: SEVERITY_CLASS_NAME_MAP[value],
      }));
    },
  },
  methods: {
    cancel() {
      this.$emit('cancel');
      this.resetState();
    },
    resetValidators() {
      this.isValidSeverity = true;
      this.isValidComment = true;
    },
    resetState() {
      this.selectedSeverity = null;
      this.comment = null;
      this.resetValidators();
    },
    setValidators() {
      this.isValidSeverity = Boolean(this.selectedSeverity);
      this.isValidComment = Boolean(this.comment);
    },
    isFormValid() {
      return Boolean(this.isValidSeverity) && Boolean(this.isValidComment);
    },
    async handleSubmit() {
      this.setValidators();
      if (!this.isFormValid()) {
        return false;
      }
      this.isSubmitting = true;
      this.$emit('clear-rejected');
      const vulnerabilityIds = this.selectedVulnerabilities.map(
        (vulnerability) => vulnerability.id,
      );
      try {
        await this.$apollo.mutate({
          mutation: vulnerabilitiesSeverityOverride,
          variables: {
            vulnerabilityIds,
            comment: this.comment,
            severity: this.selectedSeverity.toUpperCase(),
          },
          refetchQueries: [this.vulnerabilitiesQuery, this.vulnerabilitiesCountsQuery],
          awaitRefetchQueries: true,
        });
        this.$emit('vulnerabilities-updated', vulnerabilityIds);
        this.handleUpdateStateSuccess(vulnerabilityIds.length);
        return true;
      } catch (error) {
        Sentry.captureException(error);
        this.$emit('update-rejected', this.selectedVulnerabilities);
        return false;
      } finally {
        this.isSubmitting = false;
      }
    },
    handleUpdateStateSuccess(updatedVulnerabilitiesCount) {
      toast(this.$options.i18n.severityChanged(this.selectedSeverity, updatedVulnerabilitiesCount));
      this.resetState();
    },
  },
  i18n: {
    cancel: __('Cancel'),
    selected: s__('SecurityReports|%{count} Selected'),
    severityTogglePlaceholder: s__('SecurityReports|Select severity'),
    requiredCommentPlaceholder: s__(
      'SecurityReports|Add reason for the severity change (required)',
    ),
    requiredSelection: s__('SecurityReports|This selection is required.'),
    requiredComment: s__('SecurityReports|A comment is required when changing the severity.'),
    changeSeverity: s__('SecurityReports|Change severity'),
    severityChanged: (severity, count) =>
      sprintf(
        n__(
          '%{count} vulnerability set to %{severity} severity',
          '%{count} vulnerabilities set to %{severity} severity',
          count,
        ),
        {
          count,
          severity,
        },
      ),
  },
};
</script>

<template>
  <form
    class="gl-flex gl-flex-grow gl-flex-wrap gl-items-start gl-gap-3"
    @submit.prevent="handleSubmit"
  >
    <gl-form-group
      label-class="gl-hidden"
      :state="isValidSeverity"
      :invalid-feedback="$options.i18n.requiredSelection"
      class="gl-mb-0"
      data-testid="severity-form-group"
    >
      <gl-collapsible-listbox
        v-model="selectedSeverity"
        :items="severityItems"
        :toggle-text="severityToggleText"
        :disabled="isSubmitting"
        :variant="isValidSeverity ? 'default' : 'danger'"
        :category="isValidSeverity ? 'primary' : 'secondary'"
        :aria-invalid="!isValidSeverity"
        block
        :class="{ 'is-invalid gl-mb-3': !isValidSeverity }"
        data-testid="severity-listbox"
        @select="resetValidators"
      >
        <template #list-item="{ item }">
          <gl-icon
            :name="item.icon"
            :size="12"
            class="gl-mr-3"
            :class="item.iconColor"
            :data-testid="`severity-icon-${item.value}`"
          />
          <span class="gl-whitespace-nowrap">{{ item.text }}</span>
        </template>
      </gl-collapsible-listbox>
    </gl-form-group>

    <gl-form-group
      label-class="gl-hidden"
      :state="isValidComment"
      :invalid-feedback="$options.i18n.requiredComment"
      class="gl-mb-0 gl-min-w-20 gl-grow gl-basis-0"
      data-testid="comment-form-group"
    >
      <gl-form-input
        v-model="comment"
        :placeholder="$options.i18n.requiredCommentPlaceholder"
        :disabled="isSubmitting"
        :state="isValidComment"
        data-testid="comment-input"
        @input="isValidComment = true"
      />
    </gl-form-group>

    <gl-button
      class="gl-ml-auto"
      :disabled="isSubmitting"
      data-testid="cancel-button"
      @click="cancel"
    >
      {{ $options.i18n.cancel }}
    </gl-button>
    <gl-button
      data-testid="change-severity-button"
      type="submit"
      variant="confirm"
      :loading="isSubmitting"
      class="js-no-auto-disable"
    >
      {{ $options.i18n.changeSeverity }}
    </gl-button>
  </form>
</template>
